import { getMicrophonePermissionHelpUrl } from "@memorang/helpers/device";
import { isWeb } from "@memorang/helpers/platform";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Linking } from "react-native";
import { Dialog, Text } from "react-native-paper";
import { useAppTheme } from "../useAppTheme";
import Box from "./Box";
import Button from "./Button";
import DialogWrapper from "./DialogWrapper";
import { Skeleton } from "./Skeleton";

const demoGifSize = 240;
const microphoneDemoGifUri =
	"https://services.memorang.com/storage/v1/object/public/memorang-assets/icons/microphone-demo.gif";

type Props = {
	showDialog: boolean;
	setShowDialog: () => void;
} & (
	| { variant?: "default" }
	| {
			variant: "confirm-exit";
			blocked: boolean;
			onEnable: () => void;
			onExit: () => void;
	  }
);

export const MicrophonePermissionDialog = (props: Props) => {
	const { showDialog, setShowDialog } = props;
	const [demoGifLoaded, setDemoGifLoaded] = useState(false);
	const theme = useAppTheme();
	const { t } = useTranslation();
	const handleOpenSettings = () => {
		setShowDialog();
		Linking.openSettings();
	};
	const handleLearnMore = () => {
		Linking.openURL(getMicrophonePermissionHelpUrl());
	};
	const isBlockedWeb =
		props.variant === "confirm-exit" && props.blocked && isWeb;
	const getDescription = () => {
		if (!isWeb) {
			return t("session.permissionDialog.descriptionMobile");
		}
		if (props.variant !== "confirm-exit") {
			return t("session.permissionDialog.descriptionWeb");
		}
		return props.blocked
			? t("session.permissionDialog.blockedDescriptionWeb")
			: t("session.permissionDialog.promptDescriptionWeb");
	};
	return (
		<DialogWrapper visible={showDialog} handleClose={setShowDialog}>
			<Dialog.Icon icon="microphone" />
			<Dialog.Title style={{ textAlign: "center" }}>
				{t("session.permissionDialog.title")}
			</Dialog.Title>
			<Dialog.Content style={{ gap: 16 }}>
				<Text variant="bodyMedium">{getDescription()}</Text>
				{isBlockedWeb && (
					<Box
						alignSelf="center"
						width={demoGifSize}
						height={demoGifSize}
						style={{ borderRadius: 12, overflow: "hidden" }}
					>
						{!demoGifLoaded && (
							<Skeleton
								width={demoGifSize}
								height={demoGifSize}
								borderRadius={12}
							/>
						)}
						<Image
							source={{ uri: microphoneDemoGifUri }}
							onLoadEnd={() => setDemoGifLoaded(true)}
							style={{
								position: "absolute",
								width: "100%",
								height: "100%",
								opacity: demoGifLoaded ? 1 : 0,
							}}
							accessibilityIgnoresInvertColors
						/>
					</Box>
				)}
			</Dialog.Content>
			<Dialog.Actions style={{ gap: 12, flexWrap: "wrap" }}>
				{props.variant === "confirm-exit" ? (
					<>
						<Button textColor={theme.colors.primary} onPress={props.onExit}>
							{t("session.permissionDialog.dismissAndExit")}
						</Button>
						{isWeb ? (
							<>
								{props.blocked && (
									<Button
										textColor={theme.colors.primary}
										onPress={handleLearnMore}
									>
										{t("session.permissionDialog.learnMore")}
									</Button>
								)}
								<Button mode="contained" onPress={props.onEnable}>
									{t("session.permissionDialog.requestPermission")}
								</Button>
							</>
						) : (
							<Button mode="contained" onPress={handleOpenSettings}>
								{t("session.permissionDialog.openSettings")}
							</Button>
						)}
					</>
				) : (
					<>
						<Button textColor={theme.colors.primary} onPress={setShowDialog}>
							{isWeb
								? t("session.permissionDialog.ok")
								: t("session.permissionDialog.cancel")}
						</Button>
						{!isWeb && (
							<Button mode="contained" onPress={handleOpenSettings}>
								{t("session.permissionDialog.openSettings")}
							</Button>
						)}
					</>
				)}
			</Dialog.Actions>
		</DialogWrapper>
	);
};
